'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowLeft, ShoppingBag, Minus, Plus, Star, Truck, Shield, RotateCcw } from 'lucide-react';
import { ProductCard } from '@/components/shop';
import { useCartStore } from '@/store';
import { useCurrency } from '@/hooks/useCurrency';
import { Product } from '@/types';
import styles from '@/app/(shop)/products/[slug]/page.module.css';

interface ProductDetailClientProps {
    product: Product | null;
    relatedProducts: Product[];
}

export default function ProductDetailClient({ product, relatedProducts }: ProductDetailClientProps) {
    const [quantity, setQuantity] = useState(1);
    const [imgError, setImgError] = useState(false);
    const { addItem, openCart } = useCartStore();
    const { format } = useCurrency();


    if (!product) {
        return (
            <div className={styles.notFound}>
                <h1>Product Not Found</h1>
                <p>The product you are looking for does not exist.</p>
                <Link href="/products">
                    <button className="btn btn-primary">
                        <ArrowLeft size={18} />
                        Back to Products
                    </button>
                </Link>
            </div>
        );
    }

    const handleAddToCart = () => {
        if (!product.isAvailable) return;
        addItem(product, quantity);
        openCart();
    };

    const intensity = product.intensity;
    const intensityBars = intensity === null
        ? null
        : Array.from({ length: 10 }, (_, i) => (
            <span
                key={i}
                className={`${styles.intensityBar} ${i < intensity ? styles.active : ''}`}
            />
        ));
    const hasDetails = Boolean(product.origin) || product.flavorNotes.length > 0 || product.compatibility.length > 0;

    return (
        <div className={styles.page}>
            <nav className={styles.breadcrumb}>
                <div className="container">
                    <Link href="/">Home</Link>
                    <span>/</span>
                    <Link href="/products">Products</Link>
                    <span>/</span>
                    <span className={styles.current}>{product.name}</span>
                </div>
            </nav>

            <section className={styles.productSection}>
                <div className="container">
                    <div className={styles.productGrid}>
                        <motion.div
                            className={styles.imageColumn}
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <div className={styles.mainImage}>
                                {product.imageUrl && !imgError ? (
                                    <Image
                                        src={product.imageUrl}
                                        alt={product.name}
                                        fill
                                        sizes="(max-width: 900px) 100vw, 50vw"
                                        style={{ objectFit: 'contain' }}
                                        onError={() => setImgError(true)}
                                        priority
                                    />
                                ) : (
                                    <div style={{ width: '60%', opacity: 0.3 }}>
                                        <svg viewBox="0 0 60 100" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: 'auto' }}>
                                            <rect x="10" y="20" width="40" height="50" rx="5" fill="#C9A45C" />
                                            <rect x="12" y="5" width="36" height="20" rx="3" fill="#D4AF37" />
                                        </svg>
                                    </div>
                                )}

                                {product.isFeatured && (
                                    <span className={styles.featuredBadge}>
                                        <Star size={14} />
                                        Featured
                                    </span>
                                )}
                            </div>
                        </motion.div>

                        <motion.div
                            className={styles.infoColumn}
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            {product.category && <span className={styles.category}>{product.category}</span>}
                            {product.partnerName && <span className={styles.category}>{product.partnerName}</span>}
                            <h1 className={styles.productName}>{product.name}</h1>

                            <div className={styles.priceRow}>
                                <span className={styles.price}>{format(product.price)}</span>
                                {product.partnerId === null && (
                                    <span className={styles.perUnit}>per box of 10 capsules</span>
                                )}
                            </div>

                            <p className={styles.description}>
                                {product.longDescription || product.description}
                            </p>

                            {intensity !== null && (
                                <div className={styles.intensityBlock}>
                                    <div className={styles.intensityHeader}>
                                        <span>Intensity</span>
                                        <span className={styles.intensityValue}>{intensity}/10</span>
                                    </div>
                                    <div className={styles.intensityMeter}>
                                        {intensityBars}
                                    </div>
                                </div>
                            )}

                            {hasDetails && (
                                <div className={styles.details}>
                                    {product.origin && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>Origin</span>
                                            <span className={styles.detailValue}>{product.origin}</span>
                                        </div>
                                    )}
                                    {product.flavorNotes.length > 0 && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>Flavor Notes</span>
                                            <div className={styles.flavorNotes}>
                                                {product.flavorNotes.map((note) => (
                                                    <span key={note} className={styles.flavorTag}>{note}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {product.compatibility.length > 0 && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>Compatibility</span>
                                            <span className={styles.detailValue}>{product.compatibility.join(', ')}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className={styles.addToCart}>
                                <div className={styles.quantitySelector}>
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        disabled={quantity <= 1}
                                    >
                                        <Minus size={18} />
                                    </button>
                                    <span>{quantity}</span>
                                    <button onClick={() => setQuantity(quantity + 1)}>
                                        <Plus size={18} />
                                    </button>
                                </div>
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={handleAddToCart}
                                    disabled={!product.isAvailable}
                                >
                                    <ShoppingBag size={20} />
                                    {product.isAvailable
                                        ? `Add to Cart - ${format(product.price * quantity)}`
                                        : 'Out of stock'}
                                </button>
                            </div>

                            <div className={styles.benefits}>
                                <div className={styles.benefit}>
                                    <Truck size={20} />
                                    <span>Fast delivery across Lebanon</span>
                                </div>
                                <div className={styles.benefit}>
                                    <Shield size={20} />
                                    <span>Quality guaranteed</span>
                                </div>
                                <div className={styles.benefit}>
                                    <RotateCcw size={20} />
                                    <span>Easy returns</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {relatedProducts.length > 0 && (
                <section className={styles.relatedSection}>
                    <div className="container">
                        <h2>You May Also Like</h2>
                        <div className="divider" style={{ margin: 'var(--space-4) 0 var(--space-8)' }} />
                        <div className={styles.relatedGrid}>
                            {relatedProducts.map((p, index) => (
                                <ProductCard key={p.id} product={p} index={index} />
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
